/**
 * Users & Roles (admin, view.users). Slice 1 = read-only Foundation:
 * - Users: bestehende Liste + Anlegen/Bearbeiten/Löschen (unverändert) + „Rechte" → Effective-Panel.
 * - Roles: read-only Rollen-Übersicht (#User / #Permissions).
 * - Permissions: read-only Katalog (technische ID + Label, gruppiert).
 * Schreibpfade für Rollen/Permissions kommen in Slice 2/3.
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api, hasPermission } from "../store";
import { PageHead, Panel } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";
import {
  type PermissionMeta,
  type PermSource,
  effectivePermissionRows,
  groupByPrefix,
  roleMatrixRows,
  diffPermissions,
  hasPermissionChanges,
  canEditRolePermissions,
  PERMISSION_GROUP_LABEL,
  type RoleLike,
  roleAssignmentRows,
  assignedNonSystemRoleIds,
  assignedSystemRoles,
  diffRoleIds,
  hasRoleChanges,
  canEditUserRoles,
  canResetPassword,
  canCreateUser,
  createUserPayload,
} from "../users-roles";

interface PubUser { user: string; name: string; role: "admin" | "user"; servers: "*" | string[]; hasPass: boolean }
interface Draft { orig: string; user: string; name: string; role: "admin" | "user"; password: string; servers: string[] }
interface RoleSummary { id: number; name: string; label: string; permissionCount: number; userCount: number }
interface RoleDetail { id: number; name: string; label: string; editable: boolean; permissions: string[] }
interface AssignedRole { id: number; name: string; label: string }
interface Effective {
  user: string; legacyAdmin: boolean; roles: AssignedRole[]; roleDerived: string[];
  viewerFallback: boolean; directAllow: string[]; directDeny: string[]; effective: string[];
}
interface UserRolesView {
  user: { id: number; username: string; name: string; legacyRole: "admin" | "user"; legacyAdmin: boolean };
  assignedRoles: RoleLike[];
  availableRoles: RoleLike[];
  effectivePermissions: string[];
}

type Section = "users" | "roles" | "permissions";

const SOURCE_LABEL: Record<PermSource, string> = {
  "legacy-admin": "admin", deny: "−deny", allow: "+allow", role: "role", none: "—",
};

export function Users() {
  const users = useSignal<PubUser[]>([]);
  const allServers = useSignal<{ id: string; name: string }[]>([]);
  const me = useSignal("");
  const draft = useSignal<Draft | null>(null);
  const edit = useSignal(false);
  const section = useSignal<Section>("users");
  const roles = useSignal<RoleSummary[]>([]);
  const catalog = useSignal<PermissionMeta[]>([]);
  const effUser = useSignal<string | null>(null);
  const eff = useSignal<Effective | null>(null);
  const userRoles = useSignal<UserRolesView | null>(null);
  const draftRoleIds = useSignal<number[]>([]);
  const savingRoles = useSignal(false);
  const selectedRole = useSignal<string | null>(null);
  const roleDetail = useSignal<RoleDetail | null>(null);
  const roleErr = useSignal<string | null>(null);
  const draftPerms = useSignal<string[]>([]);
  const savingPerms = useSignal(false);
  // Password-Reset (Slice 15b): Confirm-Ziel, Ergebnis (Temp-PW NUR hier im
  // flüchtigen State), Loading. Niemals in localStorage/URL/Toast/Log.
  const resetTarget = useSignal<string | null>(null);
  // Temp-Credential (Reset ODER Create) — Temp-PW NUR hier im flüchtigen State.
  const tempCredential = useSignal<{ username: string; temporaryPassword: string } | null>(null);
  const resetting = useSignal(false);
  // User-Create: Minimal-Formular (kein Passwortfeld), Loading.
  const createDraft = useSignal<{ user: string; name: string } | null>(null);
  const creating = useSignal(false);

  async function load() {
    const r = await api.call<ApiResponse & { users: PubUser[]; servers: { id: string; name: string }[]; me: string }>("users_list");
    if (r.ok) { users.value = r.users; allServers.value = r.servers; me.value = r.me; }
  }
  async function loadRoles() {
    const r = await api.call<ApiResponse & { roles: RoleSummary[] }>("roles_list");
    if (r.ok) roles.value = r.roles;
  }
  async function loadCatalog() {
    const r = await api.call<ApiResponse & { permissions: PermissionMeta[] }>("permissions_list");
    if (r.ok) catalog.value = r.permissions;
  }
  async function openEff(user: string) {
    effUser.value = user; eff.value = null; userRoles.value = null; draftRoleIds.value = [];
    const [r1, r2] = await Promise.all([
      api.call<ApiResponse & { effective: Effective }>("user_effective_permissions", { user }),
      api.call<ApiResponse & UserRolesView>("user_roles_get", { user }),
    ]);
    if (r1.ok) eff.value = r1.effective;
    else { toast(String(r1.error ?? t("common.error")), "err"); effUser.value = null; return; }
    if (r2.ok) {
      const view: UserRolesView = { user: r2.user, assignedRoles: r2.assignedRoles, availableRoles: r2.availableRoles, effectivePermissions: r2.effectivePermissions };
      userRoles.value = view;
      draftRoleIds.value = assignedNonSystemRoleIds(view.assignedRoles);
    }
  }
  function closeEff() {
    effUser.value = null; eff.value = null; userRoles.value = null; draftRoleIds.value = [];
  }
  function toggleRole(id: number) {
    const cur = draftRoleIds.value;
    draftRoleIds.value = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
  }
  async function saveUserRoles() {
    const ur = userRoles.value;
    if (!ur || savingRoles.value) return;
    if (!canEditUserRoles(hasPermission("action.manage_users"), hasPermission("action.manage_roles"))) return;
    savingRoles.value = true;
    try {
      const r = await api.call<ApiResponse & { added: string[]; removed: string[] }>("user_roles_save", {
        user: ur.user.username, roleIds: draftRoleIds.value,
      });
      if (r.ok) {
        toast(`${t("usersRoles.rolesSaved")} (+${r.added.length} ${t("usersRoles.added")}, −${r.removed.length} ${t("usersRoles.removed")})`, "ok");
        await openEff(ur.user.username); // reload effective + user_roles (kein optimistisches Überschreiben)
        await loadRoles(); // Rollen-Übersicht (userCount) aktualisieren
      } else toast(String(r.error ?? t("common.error")), "err");
    } finally { savingRoles.value = false; }
  }
  async function selectRole(name: string) {
    selectedRole.value = name; roleDetail.value = null; roleErr.value = null; draftPerms.value = [];
    const r = await api.call<ApiResponse & { role: RoleDetail }>("roles_get", { role: name });
    if (r.ok) { roleDetail.value = r.role; draftPerms.value = [...r.role.permissions]; }
    else { roleErr.value = String(r.error ?? t("common.error")); }
  }
  function togglePerm(id: string) {
    const cur = draftPerms.value;
    draftPerms.value = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
  }
  async function saveRolePerms() {
    const role = roleDetail.value;
    if (!role || savingPerms.value) return;
    savingPerms.value = true;
    try {
      const r = await api.call<ApiResponse & { added: string[]; removed: string[] }>("roles_permissions_save", {
        role: role.name, permissions: draftPerms.value,
      });
      if (r.ok) {
        toast(`${t("usersRoles.permsSaved")} (+${r.added.length} ${t("usersRoles.added")}, −${r.removed.length} ${t("usersRoles.removed")})`, "ok");
        await selectRole(role.name); await loadRoles();
      } else toast(String(r.error ?? t("common.error")), "err");
    } finally { savingPerms.value = false; }
  }

  useSignalEffect(() => { void load(); void loadRoles(); void loadCatalog(); });

  // „User anlegen" öffnet das sichere Create-Formular (kein Passwortfeld); der
  // Draft-Editor ist nur noch fürs Bearbeiten bestehender User (editUser).
  const newUser = () => (createDraft.value = { user: "", name: "" });
  const editUser = (u: PubUser) => (draft.value = {
    orig: u.user, user: u.user, name: u.name, role: u.role, password: "",
    servers: u.servers === "*" ? [] : [...u.servers],
  });

  async function save() {
    const d = draft.value;
    if (!d) return;
    const r = await api.call<ApiResponse>("user_save", {
      orig: d.orig, user: d.user, name: d.name, role: d.role, password: d.password,
      servers: d.role === "admin" ? "*" : d.servers,
    });
    if (r.ok) { toast(t("common.saved"), "ok"); draft.value = null; await load(); await loadRoles(); }
    else toast(String(r.error ?? t("common.error")), "err");
  }
  async function del(u: string) {
    if (!confirm(`${t("users.confirmDeletePre")}${u}${t("users.confirmDeletePost")}`)) return;
    const r = await api.call<ApiResponse>("user_delete", { user: u });
    if (r.ok) { toast(t("common.deleted"), "ok"); await load(); await loadRoles(); } else toast(String(r.error ?? t("common.error")), "err");
  }
  async function doResetPassword() {
    const user = resetTarget.value;
    if (!user || resetting.value) return;
    resetting.value = true;
    try {
      const r = await api.call<ApiResponse & { username: string; temporaryPassword: string }>("user_password_reset", { user });
      if (r.ok) {
        resetTarget.value = null;
        tempCredential.value = { username: r.username, temporaryPassword: r.temporaryPassword };
        toast(t("usersRoles.pwResetDone"), "ok"); // niemals das Passwort im Toast
      } else {
        resetTarget.value = null; // bei Fehler KEIN Result-State
        toast(String(r.error ?? t("common.error")), "err");
      }
    } finally { resetting.value = false; }
  }
  async function doCreate() {
    const cd = createDraft.value;
    if (!cd || creating.value) return;
    creating.value = true;
    try {
      // Payload bewusst NUR { user, name } — kein password/role/servers.
      const r = await api.call<ApiResponse & { username: string; temporaryPassword: string }>("user_create", createUserPayload(cd.user, cd.name));
      if (r.ok) {
        createDraft.value = null;
        tempCredential.value = { username: r.username, temporaryPassword: r.temporaryPassword };
        toast(t("usersRoles.userCreated"), "ok"); // niemals das Passwort im Toast
        await load(); await loadRoles();
      } else {
        toast(String(r.error ?? t("common.error")), "err"); // bei Fehler KEIN Credential-State, Formular bleibt
      }
    } finally { creating.value = false; }
  }
  function closeCredential() {
    tempCredential.value = null; // Temp-PW aus dem UI-State entfernen
  }
  async function copyTempPassword() {
    const pw = tempCredential.value?.temporaryPassword;
    if (!pw) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pw);
        toast(t("usersRoles.copied"), "ok");
        return;
      }
    } catch { /* fällt unten auf manuelle Auswahl zurück */ }
    const el = document.getElementById("pw-reset-value") as HTMLInputElement | null;
    if (el) {
      el.focus(); el.select();
      try { if (document.execCommand("copy")) { toast(t("usersRoles.copied"), "ok"); return; } } catch { /* ignore */ }
    }
    toast(t("usersRoles.copyManual")); // Hinweis ohne Passwortinhalt (Default-Ton)
  }

  const d = draft.value;
  const toggleSrv = (id: string) => {
    if (!d) return;
    const has = d.servers.includes(id);
    draft.value = { ...d, servers: has ? d.servers.filter((x) => x !== id) : [...d.servers, id] };
  };
  const scopeText = (u: PubUser) => (u.role === "admin" || u.servers === "*" ? t("users.allServers") : `${(u.servers as string[]).length} ${t("users.nServersSuffix")}`);

  const userItems: PanelItem[] = [
    { id: "list", title: t("usersRoles.usersTitle"), defaultSpan: 8, render: () => (
      <Panel title={t("usersRoles.usersTitle")}>
        <table class="score">
          <thead><tr><th>Login</th><th>Name</th><th>{t("users.role")}</th><th>Scope</th><th></th></tr></thead>
          <tbody>
            {users.value.map((u) => (
              <tr key={u.user}>
                <td class="mono" data-label="Login">{u.user}{u.user === me.value && <span class="caption">{t("users.you")}</span>}</td>
                <td data-label="Name">{u.name}</td>
                <td data-label="Rolle"><span class={`badge ${u.role === "admin" ? "accent" : ""}`}>{u.role}</span></td>
                <td class="muted" data-label="Scope">{scopeText(u)}{!u.hasPass && <span class="caption">{t("usersRoles.noPassword")}</span>}</td>
                <td class="num">
                  <button class="btn sm ghost" onClick={() => void openEff(u.user)}>{t("usersRoles.viewPermissions")}</button>
                  <button class="btn sm ghost" onClick={() => editUser(u)}>{t("common.editAction")}</button>
                  {canResetPassword(hasPermission("action.manage_users")) &&
                    <button class="btn sm ghost" onClick={() => (resetTarget.value = u.user)}>{t("usersRoles.resetPassword")}</button>}
                  <button class="btn sm ghost" onClick={() => void del(u.user)}>{t("common.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    ) },
    ...(d ? [{ id: "editor", title: d.orig ? `„${d.orig}" ${t("users.editSuffix")}` : t("users.newUser"), defaultSpan: 4, render: () => (
      <Panel title={d.orig ? `„${d.orig}" ${t("users.editSuffix")}` : t("users.newUser")} actions={<button class="btn sm ghost" onClick={() => (draft.value = null)}>✕</button>}>
        <div class="stack">
          <div class="field"><label>Login</label><input class="input mono" value={d.user} onInput={(e) => (draft.value = { ...d, user: (e.target as HTMLInputElement).value })} /></div>
          <div class="field"><label>{t("users.displayName")}</label><input class="input" value={d.name} onInput={(e) => (draft.value = { ...d, name: (e.target as HTMLInputElement).value })} /></div>
          <div class="field"><label>{t("users.role")}</label>
            <select class="select" value={d.role} onChange={(e) => (draft.value = { ...d, role: (e.target as HTMLSelectElement).value as "admin" | "user" })}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div class="field"><label>{t("users.password")} {d.orig && <span class="faint">{t("servers.emptyUnchanged")}</span>}</label>
            <input class="input" type="password" autocomplete="new-password" value={d.password} onInput={(e) => (draft.value = { ...d, password: (e.target as HTMLInputElement).value })} /></div>
          {d.role === "user" && (
            <div class="field"><label>{t("users.serverAccess")}</label>
              <div class="stack" style="gap:6px">
                {allServers.value.map((s) => (
                  <label key={s.id} class="row" style="gap:8px">
                    <input type="checkbox" checked={d.servers.includes(s.id)} onChange={() => toggleSrv(s.id)} />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {d.role === "admin" && <div class="caption">{t("users.adminAllServers")}</div>}
          <button class="btn primary" onClick={() => void save()}>{t("common.save")}</button>
        </div>
      </Panel>
    ) }] as PanelItem[] : []),
  ];

  const e = eff.value;
  const ur = userRoles.value;
  const effPanel = effUser.value && (
    <Panel
      title={`${t("usersRoles.effectiveTitle")}: ${effUser.value}`}
      actions={<button class="btn sm ghost" onClick={closeEff}>✕</button>}
    >
      {!e ? <div class="muted">{t("common.loading")}</div> : (
        <div class="stack" style="gap:12px">
          {e.legacyAdmin && <div class="badge accent">{t("usersRoles.legacyAdminNote")}</div>}
          <div class="field"><label>{t("usersRoles.granularRoles")}</label>
            {e.roles.length === 0
              ? <span class="muted">{e.viewerFallback ? t("usersRoles.viewerFallback") : t("usersRoles.noRoles")}</span>
              : <div class="row" style="gap:6px;flex-wrap:wrap">{e.roles.map((r) => <span key={r.id} class="badge">{r.label || r.name}</span>)}</div>}
          </div>
          {ur && (() => {
            const canEditRoles = canEditUserRoles(hasPermission("action.manage_users"), hasPermission("action.manage_roles"));
            const initialIds = assignedNonSystemRoleIds(ur.assignedRoles);
            const rows = roleAssignmentRows(ur.availableRoles, draftRoleIds.value);
            const sysRoles = assignedSystemRoles(ur.assignedRoles);
            const dirty = hasRoleChanges(initialIds, draftRoleIds.value);
            const rdiff = diffRoleIds(initialIds, draftRoleIds.value);
            return (
              <div class="field">
                <label>{t("usersRoles.assignRolesTitle")}</label>
                <div class="caption">{t("usersRoles.legacyRole")}: <span class="mono">{ur.user.legacyRole}</span></div>
                {ur.user.legacyAdmin && <div class="badge accent">{t("usersRoles.legacyAdminAssignNote")}</div>}
                {!canEditRoles && <div class="badge">{t("usersRoles.needBothManage")}</div>}
                {sysRoles.length > 0 && (
                  <div class="row" style="gap:6px;flex-wrap:wrap;margin:4px 0">
                    {sysRoles.map((r) => (
                      <span key={r.id} class="badge" title={t("usersRoles.systemRolesNotAssignable")}>
                        {r.label || r.name} <span class="caption">{t("usersRoles.systemBadge")}</span>
                      </span>
                    ))}
                  </div>
                )}
                {rows.length === 0
                  ? <span class="muted">{t("usersRoles.noAssignableRoles")}</span>
                  : (
                    <div class="role-assign-grid">
                      {rows.map((r) => (
                        <label key={r.id} class={`perm-row${canEditRoles ? "" : " is-readonly"}`}>
                          <input type="checkbox" checked={r.checked} disabled={!canEditRoles} onChange={() => toggleRole(r.id)} />
                          <span class="perm-text"><span>{r.label}</span><span class="mono caption perm-id">{r.name}</span></span>
                        </label>
                      ))}
                    </div>
                  )}
                {canEditRoles && (
                  <div class="roles-save-bar">
                    <button class="btn primary" disabled={!dirty || savingRoles.value} onClick={() => void saveUserRoles()}>{t("usersRoles.saveRoles")}</button>
                    <span class="caption">{dirty ? `${t("usersRoles.unsavedChanges")}: +${rdiff.added.length} / −${rdiff.removed.length}` : t("usersRoles.noChanges")}</span>
                  </div>
                )}
              </div>
            );
          })()}
          {(e.directAllow.length > 0 || e.directDeny.length > 0) && (
            <div class="field"><label>{t("usersRoles.directOverrides")}</label>
              <div class="row" style="gap:6px;flex-wrap:wrap">
                {e.directAllow.map((p) => <span key={`a${p}`} class="badge ok mono">+{p}</span>)}
                {e.directDeny.map((p) => <span key={`d${p}`} class="badge danger mono">−{p}</span>)}
              </div>
            </div>
          )}
          <div class="field"><label>{t("usersRoles.effectivePermissions")} ({e.effective.length})</label>
            {groupByPrefix(effectivePermissionRows(catalog.value, e)).map((grp) => (
              <div key={grp.group} class="stack" style="gap:3px;margin-bottom:8px">
                <div class="caption mono">{PERMISSION_GROUP_LABEL[grp.group]}</div>
                {grp.items.filter((r) => r.granted).map((r) => (
                  <div key={r.id} class="row" style="gap:8px;align-items:baseline">
                    <span class="mono" style="min-width:230px">{r.id}</span>
                    <span class="muted" style="flex:1">{r.label}</span>
                    <span class={`badge sm ${r.source === "deny" ? "danger" : r.source === "allow" ? "ok" : ""}`}>{SOURCE_LABEL[r.source]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );

  const resetConfirmPanel = resetTarget.value && (
    <Panel
      title={`${t("usersRoles.resetPassword")}: ${resetTarget.value}`}
      actions={<button class="btn sm ghost" onClick={() => (resetTarget.value = null)}>✕</button>}
    >
      <div class="stack" style="gap:12px">
        <div class="badge warn">{t("usersRoles.resetConfirmHint")}</div>
        <div class="row" style="gap:8px">
          <button class="btn primary" disabled={resetting.value} onClick={() => void doResetPassword()}>{t("usersRoles.resetConfirmAction")}</button>
          <button class="btn ghost" disabled={resetting.value} onClick={() => (resetTarget.value = null)}>{t("common.cancel")}</button>
        </div>
      </div>
    </Panel>
  );

  const cd = createDraft.value;
  const createPanel = cd && (
    <Panel
      title={t("usersRoles.createUser")}
      actions={<button class="btn sm ghost" onClick={() => (createDraft.value = null)}>✕</button>}
    >
      <div class="stack" style="gap:12px">
        <div class="field"><label>Login</label>
          <input class="input mono" value={cd.user} onInput={(e) => (createDraft.value = { ...cd, user: (e.target as HTMLInputElement).value })} /></div>
        <div class="field"><label>{t("users.displayName")} <span class="faint">{t("usersRoles.optional")}</span></label>
          <input class="input" value={cd.name} onInput={(e) => (createDraft.value = { ...cd, name: (e.target as HTMLInputElement).value })} /></div>
        <div class="badge warn">{t("usersRoles.createHint")}</div>
        <div class="row" style="gap:8px">
          <button class="btn primary" disabled={creating.value || cd.user.trim() === ""} onClick={() => void doCreate()}>{t("usersRoles.createSubmit")}</button>
          <button class="btn ghost" disabled={creating.value} onClick={() => (createDraft.value = null)}>{t("common.cancel")}</button>
        </div>
      </div>
    </Panel>
  );

  const cred = tempCredential.value;
  const credentialPanel = cred && (
    <Panel
      title={`${t("usersRoles.tempPasswordTitle")}: ${cred.username}`}
      actions={<button class="btn sm ghost" onClick={closeCredential}>✕</button>}
    >
      <div class="stack" style="gap:10px">
        <div class="pw-reset-box">
          <input id="pw-reset-value" class="input mono pw-reset-value" readOnly value={cred.temporaryPassword} onFocus={(e) => (e.target as HTMLInputElement).select()} />
          <button class="btn sm primary" onClick={() => void copyTempPassword()}>{t("usersRoles.copy")}</button>
        </div>
        <div class="caption danger">{t("usersRoles.pwResetOnce")}</div>
        <div class="row"><button class="btn ghost" onClick={closeCredential}>{t("common.close")}</button></div>
      </div>
    </Panel>
  );

  const canManageRoles = hasPermission("action.manage_roles");

  const rolesTablePanel = (
    <Panel title={t("usersRoles.rolesTitle")}>
      <table class="score">
        <thead><tr><th>{t("usersRoles.roleName")}</th><th>Label</th><th class="num">{t("usersRoles.userCount")}</th><th class="num">{t("usersRoles.permissionCount")}</th></tr></thead>
        <tbody>
          {roles.value.map((r) => (
            <tr key={r.id} class={`role-row${selectedRole.value === r.name ? " is-selected" : ""}`} onClick={() => void selectRole(r.name)}>
              <td class="mono" data-label="Role">{r.name}</td>
              <td data-label="Label">{r.label}</td>
              <td class="num" data-label="Users">{r.userCount}</td>
              <td class="num" data-label="Permissions">{r.permissionCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div class="caption">{t("usersRoles.rolesReadonlyNote")}</div>
    </Panel>
  );

  const rd = roleDetail.value;
  const editable = canEditRolePermissions(rd, canManageRoles);
  const dirty = !!rd && hasPermissionChanges(rd.permissions, draftPerms.value);
  const permDiff = rd ? diffPermissions(rd.permissions, draftPerms.value) : { added: [], removed: [] };

  const matrixPanel = selectedRole.value && (
    <Panel
      title={`${t("usersRoles.matrixTitle")}: ${rd ? rd.label || rd.name : selectedRole.value}`}
      actions={<button class="btn sm ghost" onClick={() => { selectedRole.value = null; roleDetail.value = null; roleErr.value = null; }}>✕</button>}
    >
      {roleErr.value
        ? <div class="badge danger">{roleErr.value}</div>
        : !rd
          ? <div class="muted">{t("common.loading")}</div>
          : (
            <div class="stack" style="gap:12px">
              <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center">
                <span class="mono badge">{rd.name}</span>
                {rd.editable === false && <span class="badge accent">{t("usersRoles.systemRoleNote")}</span>}
                {rd.editable !== false && !canManageRoles && <span class="badge">{t("usersRoles.noManageRoles")}</span>}
              </div>
              <div class="perm-scroll">
                {groupByPrefix(roleMatrixRows(catalog.value, draftPerms.value)).map((grp) => (
                  <div key={grp.group} class="stack" style="gap:2px">
                    <div class="caption mono">{PERMISSION_GROUP_LABEL[grp.group]} ({grp.items.length})</div>
                    <div class="perm-grid">
                      {grp.items.map((r) => (
                        <label key={r.id} class={`perm-row${editable ? "" : " is-readonly"}`}>
                          <input type="checkbox" checked={r.checked} disabled={!editable} onChange={() => togglePerm(r.id)} />
                          <span class="perm-text">
                            <span>{r.label}</span>
                            <span class="mono caption perm-id">{r.id}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {editable && (
                <div class="roles-save-bar">
                  <button class="btn primary" disabled={!dirty || savingPerms.value} onClick={() => void saveRolePerms()}>{t("usersRoles.savePermissions")}</button>
                  <span class="caption">
                    {dirty ? `${t("usersRoles.unsavedChanges")}: +${permDiff.added.length} / −${permDiff.removed.length}` : t("usersRoles.noChanges")}
                  </span>
                </div>
              )}
            </div>
          )}
    </Panel>
  );

  const rolesSection = (
    <div class="roles-layout">
      {rolesTablePanel}
      {matrixPanel}
    </div>
  );

  const permissionsPanel = (
    <Panel title={t("usersRoles.permissionsTitle")}>
      {groupByPrefix(catalog.value).map((grp) => (
        <div key={grp.group} class="stack" style="gap:2px;margin-bottom:12px">
          <div class="caption mono">{PERMISSION_GROUP_LABEL[grp.group]} ({grp.items.length})</div>
          {grp.items.map((p) => (
            <div key={p.id} class="row" style="gap:8px;align-items:baseline">
              <span class="mono" style="min-width:230px">{p.id}</span>
              <span class="muted">{p.label}</span>
            </div>
          ))}
        </div>
      ))}
      <div class="caption">{t("usersRoles.permissionsReadonlyNote")}</div>
    </Panel>
  );

  const seg = (id: Section, label: string) => (
    <button class={`btn sm ${section.value === id ? "primary" : "ghost"}`} onClick={() => (section.value = id)}>{label}</button>
  );

  return (
    <div class="view-enter">
      <PageHead title={t("view.users")} sub={t("usersRoles.sub")}>
        {seg("users", t("usersRoles.tabUsers"))}
        {seg("roles", t("usersRoles.tabRoles"))}
        {seg("permissions", t("usersRoles.tabPermissions"))}
        {section.value === "users" && <>
          <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
          {canCreateUser(hasPermission("action.manage_users")) &&
            <button class="btn primary" onClick={newUser}>{t("usersRoles.createUser")}</button>}
        </>}
      </PageHead>
      {section.value === "users" && <>
        <PanelGrid view="users" items={userItems} edit={edit} />
        {createPanel}
        {resetConfirmPanel}
        {credentialPanel}
        {effPanel}
      </>}
      {section.value === "roles" && rolesSection}
      {section.value === "permissions" && permissionsPanel}
    </div>
  );
}
